import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { effectText, type MergeGroup } from '../../lib/email-merge.js';
import { Button, useConfirm } from '../../components/ui/index.js';
import { errorMessage, useIsAdmin } from './common.js';

/**
 * Emailed-report sources that are really one report (an emailed report whose
 * file name carried the day used to make a new source every day). Admin only:
 * it shows each set with what merging it does to the money, and merges one set
 * on a confirmed click. Nothing is shown when there is nothing to merge.
 */
export function DuplicateEmailSources() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const dups = useQuery({
    queryKey: ['email-source-duplicates'],
    queryFn: () => api.get<{ groups: MergeGroup[] }>('/api/email-source-duplicates'),
    enabled: isAdmin,
  });
  const merge = useMutation({
    mutationFn: (g: MergeGroup) =>
      api.post('/api/email-source-duplicates/merge', { target: g.target, members: g.members.map((m) => m.id) }),
    // Figures everywhere may have moved.
    onSettled: () => void qc.invalidateQueries(),
  });

  const groups = dups.data?.groups ?? [];
  if (!isAdmin || !groups.length) return null;

  const run = async (g: MergeGroup) => {
    const ok = await confirm<boolean>({
      title: `Merge ${g.members.length} sources into one?`,
      body: (
        <>
          <p>{effectText(g)}</p>
          <p>
            Their feeds, links and history move to <code className="mono">{g.target}</code>, and the emptied sources are
            removed. This changes stored figures and can only be undone from a backup, so make sure a recent one exists
            first (RUNBOOK → Backups).
          </p>
        </>
      ),
      choices: [{ label: `Merge ${g.members.length} sources`, value: true, variant: 'danger' }],
    });
    if (ok) merge.mutate(g);
  };

  return (
    <>
      {groups.map((g) => (
        <div key={g.target} className="cfg-callout src-error" role="status">
          <b>{g.members.length} sources are really one report.</b> They all took the same emailed report, one source per
          day. {effectText(g)}
          <ul>
            {g.members.map((m) => (
              <li key={m.id}>
                <code className="mono">{m.id}</code> —{' '}
                {m.days ? `${m.days} day${m.days === 1 ? '' : 's'}, ${m.firstDay} → ${m.lastDay}` : 'no days'}
                {m.links ? `, ${m.links} link${m.links === 1 ? '' : 's'}` : ''}
                {m.enabled ? '' : ', paused'}
              </li>
            ))}
          </ul>
          {g.conflicts.length ? (
            <p>
              <b>Not safe to merge yet:</b> {g.conflicts.join('; ')}.
            </p>
          ) : (
            <Button variant="danger" size="sm" disabled={merge.isPending} onClick={() => void run(g)}>
              {merge.isPending ? 'Merging…' : `Merge into ${g.target}`}
            </Button>
          )}
        </div>
      ))}
      {merge.isError ? <p className="ui-field__error">{errorMessage(merge.error)}</p> : null}
      {dialog}
    </>
  );
}
